import { UnorderedList, ListItem } from "@chakra-ui/react";
import { visibleLength } from "../../../../../../common/text/visibleLength";
import { useTranslation } from "react-i18next";
import {
  type FieldStatusDto,
  type SourcingEventDto,
  type Public,
} from "../../../../../../autogen/bff-api";
import { useCpvCodes } from "../../../../../../common/cpv/useCpvCodes";
import { momentLocaleFor } from "../../../../../../common/formatting/i18nFormatters";
import { TipTap } from "../../../../../../common/input/TipTap/TipTap";
import type { ReadinessLookup, KnownFieldKey } from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../../../../../common/sourcing-events/buyer/wizardSteps";
import { formatContractDuration } from "../../../../portal/utils/displayFormatters";
import { formatYesNo } from "../../utils/displayFormatters";
import { displaySum } from "../../../../../../common/formatting/displaySum";
import { frameworkMechanismLabel, isFrameworkEnabled } from "../../../../../../common/procurement/publicProcurementOptions";
import { useReservedParticipationLabel } from "../../utils/useReservedParticipationLabel";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { SummaryGroupHeader } from "./SummaryGroupHeader";
import { SummarySection } from "./SummarySection";
import { frameworkDurationLimitExceeded, frameworkLimitWords } from "../../utils/frameworkDuration";
import { offersExtensions } from "../../utils/procedure";
import { CpvCodeTags } from "./CpvCodeTags";

export const FIELD_KEYS = [
  "DescriptionPresent",
  "MainCpvCodeSet",
  "MainCpvCodeValid",
  "ContractDurationTypeSet",
  "OpenEndedStartDatePresent",
  "FrameworkAgreementSet",
  "FrameworkMaxParticipantsRequiredWhenSet",
  "FrameworkMaximumValueValid",
  "FrameworkOverFourYearsReasoned",
  "EuFundedSet",
  "GpaCoverageSet",
  "ReservedParticipationSet",
  "ReservedExecutionSet",
  "ContractStartBeforeEnd",
  "ContractEndsAfterBidSubmissionDeadline",
  "ContractStartsAfterBidSubmissionDeadline",
  "ExtensionDeadlinesWithinContract",
  "ExtensionsPresentWithOptions",
  "MainCpvCodeMatchesReservedParticipation",
  "ReservedParticipationDurationWithinThreeYears",
  "MainCpvCodeMatchesContractNature",
] as const satisfies readonly KnownFieldKey[];

interface ClassificationSectionProps {
  event: SourcingEventDto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const ClassificationSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
}: ClassificationSectionProps) => {
  const { t, i18n } = useTranslation();
  const { eventData } = event;
  const publicContext: Public | null = event.eventContext.type === "Public" ? event.eventContext : null;
  const durationLimit = frameworkDurationLimitExceeded(event);
  const reservedParticipationLabel = useReservedParticipationLabel();
  const momentLocale = momentLocaleFor(i18n.language);

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  const optionsWithoutExtensions = readinessFor("ExtensionsPresentWithOptions");
  const extensionsStatus =
    optionsWithoutExtensions?.satisfied === false
      ? optionsWithoutExtensions
      : readinessFor("ExtensionDeadlinesWithinContract");

  const cpvCodes = publicContext?.cpvCodes ?? [];
  const optionDescriptions = (eventData.options?.descriptions ?? []).filter((d) => d.trim() !== "");
  const frameworkOn = isFrameworkEnabled(publicContext?.frameworkAgreement);
  const frameworkAnswer =
    publicContext?.frameworkAgreement == null ? (
      <EmptyValue text={t("Not set")} />
    ) : frameworkOn ? (
      t("Yes")
    ) : (
      t("No")
    );
  const reservedParticipation = publicContext?.reservedParticipation
    ? reservedParticipationLabel(publicContext.reservedParticipation)
    : null;
  const reservedExecution = publicContext?.reservedExecution ? t(publicContext.reservedExecution) : null;
  const euFunded = formatYesNo(publicContext?.euFunded, t);
  const gpaCoverage = formatYesNo(publicContext?.gpaCoverage, t);
  const hasOptions = formatYesNo(eventData.options?.hasOptions ?? null, t);
  const showsOptions = offersExtensions(publicContext?.procedureType, eventData.contractDuration);

  return (
    <SummarySection
      title={t("Classification and contract")}
      editTarget={{ kind: "wizard-step", step: "classification" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryGroupHeader isFirst>{t("General information")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Description")}
          sourcePaths={["eventData.description"]}
          value={
            eventData.description
              ? <TipTap content={eventData.description} editable={false} hasBorder={false} padding="0" />
              : <EmptyValue text={t("Not provided")} />
          }
          status={readinessFor("DescriptionPresent")}
        />
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Classification and value")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("CPV codes")}
          sourcePaths={["eventContext.cpvCodes", "eventContext.cpvClassification"]}
          value={
            cpvCodes.length > 0 ? (
              <CpvCodeTags codes={cpvCodes} />
            ) : (
              <EmptyValue text={t("Not set")} />
            )
          }
          status={readinessFor("MainCpvCodeSet")}
          extraStatuses={[
            readinessFor("MainCpvCodeValid"),
            readinessFor("MainCpvCodeMatchesReservedParticipation"),
            readinessFor("MainCpvCodeMatchesContractNature"),
          ]}
        />
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Contract period")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Contract duration")}
          sourcePaths={["eventData.contractDuration"]}
          value={
            eventData.contractDuration
              ? formatContractDuration(eventData.contractDuration, t, momentLocale)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("ContractDurationTypeSet")}
          extraStatuses={[
            readinessFor("OpenEndedStartDatePresent"),
            readinessFor("ContractStartBeforeEnd"),
            readinessFor("ContractEndsAfterBidSubmissionDeadline"),
            readinessFor("ContractStartsAfterBidSubmissionDeadline"),
            readinessFor("ReservedParticipationDurationWithinThreeYears"),
          ]}
        />
        {showsOptions && (
          <SummaryField
            label={t("Contract options")}
            sourcePaths={["eventData.options.hasOptions"]}
            value={hasOptions ?? <EmptyValue text={t("Not set")} />}
            span="half"
          />
        )}
        {showsOptions && eventData.options?.hasOptions === true && (
          <SummaryField
            label={t("Extensions")}
            sourcePaths={["eventData.options.extensions"]}
            value={eventData.options.renewalDescription ?? <EmptyValue text={t("No extensions registered")} />}
            status={extensionsStatus}
          />
        )}
        {showsOptions && eventData.options?.hasOptions === true && optionDescriptions.length > 0 && (
          <SummaryField
            label={t("Option text from before")}
            sourcePaths={["eventData.options.descriptions"]}
            value={
              <UnorderedList spacing="1" pl="4">
                {optionDescriptions.map((d, i) => (
                  <ListItem key={i} fontSize="sm">{d}</ListItem>
                ))}
              </UnorderedList>
            }
          />
        )}
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Framework agreement")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Framework agreement")}
          sourcePaths={["eventContext.frameworkAgreement"]}
          value={frameworkAnswer}
          span="half"
          status={readinessFor("FrameworkAgreementSet")}
        />
        {frameworkOn && publicContext?.frameworkAgreement && (
          <SummaryField
            label={t("Call-off mechanism")}
            value={frameworkMechanismLabel(publicContext.frameworkAgreement, t)}
            span="half"
          />
        )}
        {frameworkOn && (
          <SummaryField
            label={t("Maximum number of suppliers")}
            sourcePaths={["eventContext.frameworkMaxParticipants"]}
            value={
              publicContext?.frameworkMaxParticipants != null
                ? String(publicContext.frameworkMaxParticipants)
                : <EmptyValue text={t("Not set")} />
            }
            span="half"
            status={readinessFor("FrameworkMaxParticipantsRequiredWhenSet")}
          />
        )}
        {frameworkOn && (
          <SummaryField
            label={t("Maximum value")}
            sourcePaths={["eventContext.frameworkMaximumValue"]}
            value={
              publicContext?.frameworkMaximumValue?.value != null
                ? displaySum({
                    value: publicContext.frameworkMaximumValue.value,
                    currency: publicContext.frameworkMaximumValue.currency,
                  })
                : <EmptyValue text={t("Not set")} />
            }
            span="half"
            status={readinessFor("FrameworkMaximumValueValid")}
          />
        )}
        {durationLimit && (
          <SummaryField
            label={t("Special circumstances that justify a framework agreement over {{period}}", frameworkLimitWords(t, durationLimit))}
            sourcePaths={["eventContext.frameworkOverFourYearsJustification"]}
            value={
              visibleLength(publicContext?.frameworkOverFourYearsJustification ?? "") > 0 ? (
                publicContext?.frameworkOverFourYearsJustification
              ) : (
                <EmptyValue text={t("Not set")} />
              )
            }
            status={readinessFor("FrameworkOverFourYearsReasoned")}
          />
        )}
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Advanced settings")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("EU financing")}
          sourcePaths={["eventContext.euFunded"]}
          value={euFunded ?? <EmptyValue text={t("Not set")} />}
          span="half"
          status={readinessFor("EuFundedSet")}
        />
        <SummaryField
          label={t("GPA coverage")}
          sourcePaths={["eventContext.gpaCoverage"]}
          value={gpaCoverage ?? <EmptyValue text={t("Not set")} />}
          span="half"
          status={readinessFor("GpaCoverageSet")}
        />
        <SummaryField
          label={t("Reserved participation")}
          sourcePaths={["eventContext.reservedParticipation"]}
          value={reservedParticipation ?? <EmptyValue text={t("Not set")} />}
          span="half"
          status={readinessFor("ReservedParticipationSet")}
        />
        <SummaryField
          label={t("Reserved execution")}
          sourcePaths={["eventContext.reservedExecution"]}
          value={reservedExecution ?? <EmptyValue text={t("Not set")} />}
          span="half"
          status={readinessFor("ReservedExecutionSet")}
        />
      </SummaryFieldGroup>
    </SummarySection>
  );
};
